import { useEffect, useMemo, useState } from "react";

import type { AnalyticsSummary, KnowledgeDocument, WidgetConfig } from "@shared/types";

import { apiFetch } from "@web/lib/api";

type MeResponse = {
  admin: { email: string; role: string };
  tenant: {
    id: number;
    name: string;
    slug: string;
    publicWidgetKey: string;
    widgetConfig: WidgetConfig | null;
  };
};

const defaultConfig: Omit<WidgetConfig, "tenantId" | "updatedAt"> = {
  themeName: "sunrise",
  primaryColor: "#1747FF",
  accentColor: "#F4B942",
  headerTitle: "Ask us anything",
  welcomeText: "Hello. Ask anything about this business and I will answer from the approved knowledge base.",
  starterPrompts: ["What are your opening hours?", "What services do you offer?", "How can I contact support?"],
  disclaimer: "",
  allowedDomains: []
};

export function AdminConsole() {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [loginEmail, setLoginEmail] = useState("demo@example.com");
  const [loginPassword, setLoginPassword] = useState("demo123456");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draftDocId, setDraftDocId] = useState<number | null>(null);
  const [docTitle, setDocTitle] = useState("");
  const [docRawText, setDocRawText] = useState("");
  const [docSourceType, setDocSourceType] = useState<"manual" | "markdown" | "html" | "import">("manual");
  const [tenantName, setTenantName] = useState("");
  const [tenantSlug, setTenantSlug] = useState("");
  const [themeName, setThemeName] = useState(defaultConfig.themeName);
  const [primaryColor, setPrimaryColor] = useState(defaultConfig.primaryColor);
  const [accentColor, setAccentColor] = useState(defaultConfig.accentColor);
  const [headerTitle, setHeaderTitle] = useState(defaultConfig.headerTitle);
  const [welcomeText, setWelcomeText] = useState(defaultConfig.welcomeText);
  const [starterPrompts, setStarterPrompts] = useState(defaultConfig.starterPrompts.join("\n"));
  const [disclaimer, setDisclaimer] = useState(defaultConfig.disclaimer);
  const [allowedDomains, setAllowedDomains] = useState("");

  const embedSnippet = useMemo(() => {
    if (!me?.tenant?.slug) {
      return "";
    }
    return `<script src="${window.location.origin}/embed/widget.js" data-tenant="${me.tenant.slug}" defer></script>`;
  }, [me?.tenant?.slug]);

  async function loadDashboard() {
    const [mePayload, docsPayload, analyticsPayload] = await Promise.all([
      apiFetch<MeResponse>("/api/admin/me"),
      apiFetch<{ documents: KnowledgeDocument[] }>("/api/admin/documents"),
      apiFetch<{ summary: AnalyticsSummary }>("/api/admin/analytics/summary")
    ]);

    setMe(mePayload);
    setDocuments(docsPayload.documents);
    setSummary(analyticsPayload.summary);

    const widgetConfig = mePayload.tenant.widgetConfig;
    setTenantName(mePayload.tenant.name);
    setTenantSlug(mePayload.tenant.slug);
    setThemeName(widgetConfig?.themeName || defaultConfig.themeName);
    setPrimaryColor(widgetConfig?.primaryColor || defaultConfig.primaryColor);
    setAccentColor(widgetConfig?.accentColor || defaultConfig.accentColor);
    setHeaderTitle(widgetConfig?.headerTitle || defaultConfig.headerTitle);
    setWelcomeText(widgetConfig?.welcomeText || defaultConfig.welcomeText);
    setStarterPrompts((widgetConfig?.starterPrompts || defaultConfig.starterPrompts).join("\n"));
    setDisclaimer(widgetConfig?.disclaimer || defaultConfig.disclaimer);
    setAllowedDomains((widgetConfig?.allowedDomains || []).join("\n"));
  }

  useEffect(() => {
    void loadDashboard().catch(() => undefined);
  }, []);

  async function login() {
    setBusy(true);
    setError(null);
    try {
      await apiFetch("/api/admin/login", {
        method: "POST",
        body: JSON.stringify({
          email: loginEmail,
          password: loginPassword
        })
      });
      await loadDashboard();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Login failed");
    } finally {
      setBusy(false);
    }
  }

  async function logout() {
    await apiFetch("/api/admin/logout", { method: "POST" });
    setMe(null);
    setDocuments([]);
    setSummary(null);
  }

  async function saveTenant() {
    setBusy(true);
    setError(null);
    try {
      await apiFetch("/api/admin/tenant", {
        method: "PATCH",
        body: JSON.stringify({
          name: tenantName,
          slug: tenantSlug,
          widgetConfig: {
            themeName,
            primaryColor,
            accentColor,
            headerTitle,
            welcomeText,
            starterPrompts: starterPrompts
              .split("\n")
              .map((item) => item.trim())
              .filter(Boolean),
            disclaimer,
            allowedDomains: allowedDomains
              .split("\n")
              .map((item) => item.trim())
              .filter(Boolean)
          }
        })
      });
      await loadDashboard();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Failed to save tenant config");
    } finally {
      setBusy(false);
    }
  }

  async function saveDocument() {
    setBusy(true);
    setError(null);
    try {
      const path = draftDocId ? `/api/admin/documents/${draftDocId}` : "/api/admin/documents";
      const method = draftDocId ? "PATCH" : "POST";
      await apiFetch(path, {
        method,
        body: JSON.stringify({
          title: docTitle,
          rawText: docRawText,
          sourceType: docSourceType,
          status: "indexed"
        })
      });
      setDraftDocId(null);
      setDocTitle("");
      setDocRawText("");
      setDocSourceType("manual");
      await loadDashboard();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Failed to save document");
    } finally {
      setBusy(false);
    }
  }

  async function removeDocument(documentId: number) {
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/admin/documents/${documentId}`, {
        method: "DELETE"
      });
      if (draftDocId === documentId) {
        setDraftDocId(null);
        setDocTitle("");
        setDocRawText("");
      }
      await loadDashboard();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Failed to delete document");
    } finally {
      setBusy(false);
    }
  }

  async function reindex(documentId: number) {
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/admin/documents/${documentId}/reindex`, {
        method: "POST"
      });
      await loadDashboard();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Failed to reindex document");
    } finally {
      setBusy(false);
    }
  }

  if (!me) {
    return (
      <div className="admin-shell">
        <section className="login-card">
          <p className="eyebrow">Cloudflare Chatbot</p>
          <h1>Admin Console</h1>
          <p>Sign in to edit the widget, manage documents, and review chat analytics.</p>
          <label>
            Email
            <input value={loginEmail} onChange={(event) => setLoginEmail(event.target.value)} />
          </label>
          <label>
            Password
            <input
              type="password"
              value={loginPassword}
              onChange={(event) => setLoginPassword(event.target.value)}
            />
          </label>
          <button type="button" disabled={busy} onClick={() => void login()}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
          {error ? <p className="widget-error">{error}</p> : null}
        </section>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <header className="admin-hero">
        <div>
          <p className="eyebrow">Tenant workspace</p>
          <h1>{me.tenant.name}</h1>
          <p>{me.admin.email}</p>
        </div>
        <button type="button" className="ghost-button" onClick={() => void logout()}>
          Log out
        </button>
      </header>

      <section className="stats-grid">
        <article className="stat-card">
          <span>Sessions</span>
          <strong>{summary?.totalSessions || 0}</strong>
        </article>
        <article className="stat-card">
          <span>Messages</span>
          <strong>{summary?.totalMessages || 0}</strong>
        </article>
        <article className="stat-card">
          <span>Documents</span>
          <strong>{summary?.documentCount || 0}</strong>
        </article>
        <article className="stat-card">
          <span>Indexed chunks</span>
          <strong>{summary?.indexedChunkCount || 0}</strong>
        </article>
      </section>

      <section className="panel-grid">
        <article className="panel">
          <div className="panel-header">
            <div>
              <p className="eyebrow">Widget config</p>
              <h2>Brand + behavior</h2>
            </div>
            <button type="button" onClick={() => void saveTenant()} disabled={busy}>
              Save
            </button>
          </div>

          <div className="form-grid">
            <label>
              Tenant name
              <input value={tenantName} onChange={(event) => setTenantName(event.target.value)} />
            </label>
            <label>
              Slug
              <input value={tenantSlug} onChange={(event) => setTenantSlug(event.target.value)} />
            </label>
            <label>
              Theme name
              <input value={themeName} onChange={(event) => setThemeName(event.target.value)} />
            </label>
            <label>
              Primary color
              <input value={primaryColor} onChange={(event) => setPrimaryColor(event.target.value)} />
            </label>
            <label>
              Accent color
              <input value={accentColor} onChange={(event) => setAccentColor(event.target.value)} />
            </label>
            <label>
              Header title
              <input value={headerTitle} onChange={(event) => setHeaderTitle(event.target.value)} />
            </label>
          </div>

          <label>
            Welcome text
            <textarea rows={4} value={welcomeText} onChange={(event) => setWelcomeText(event.target.value)} />
          </label>
          <label>
            Starter prompts
            <textarea rows={4} value={starterPrompts} onChange={(event) => setStarterPrompts(event.target.value)} />
          </label>
          <label>
            Disclaimer
            <textarea rows={2} value={disclaimer} onChange={(event) => setDisclaimer(event.target.value)} />
          </label>
          <label>
            Allowed domains
            <textarea rows={4} value={allowedDomains} onChange={(event) => setAllowedDomains(event.target.value)} />
          </label>

          <div className="embed-block">
            <p className="eyebrow">Embed</p>
            <pre>{embedSnippet}</pre>
          </div>
        </article>

        <article className="panel">
          <div className="panel-header">
            <div>
              <p className="eyebrow">Knowledge base</p>
              <h2>Documents</h2>
            </div>
            <button type="button" onClick={() => {
              setDraftDocId(null);
              setDocTitle("");
              setDocRawText("");
              setDocSourceType("manual");
            }}>
              New
            </button>
          </div>

          <div className="documents-layout">
            <aside className="doc-list">
              {documents.map((document) => (
                <button
                  key={document.id}
                  type="button"
                  className={`doc-item ${draftDocId === document.id ? "doc-item-active" : ""}`}
                  onClick={() => {
                    setDraftDocId(document.id);
                    setDocTitle(document.title);
                    setDocRawText(document.rawText);
                    setDocSourceType(document.sourceType as "manual" | "markdown" | "html" | "import");
                  }}
                >
                  <strong>{document.title}</strong>
                  <span>{document.status}</span>
                </button>
              ))}
            </aside>

            <div className="doc-editor">
              <label>
                Title
                <input value={docTitle} onChange={(event) => setDocTitle(event.target.value)} />
              </label>
              <label>
                Source type
                <select value={docSourceType} onChange={(event) => setDocSourceType(event.target.value as typeof docSourceType)}>
                  <option value="manual">Manual</option>
                  <option value="markdown">Markdown</option>
                  <option value="html">HTML</option>
                  <option value="import">Import</option>
                </select>
              </label>
              <label>
                Content
                <textarea rows={16} value={docRawText} onChange={(event) => setDocRawText(event.target.value)} />
              </label>
              <div className="action-row">
                <button type="button" disabled={busy || !docTitle || !docRawText} onClick={() => void saveDocument()}>
                  {draftDocId ? "Update document" : "Create document"}
                </button>
                {draftDocId ? (
                  <>
                    <button type="button" className="ghost-button" onClick={() => void reindex(draftDocId)}>
                      Reindex
                    </button>
                    <button type="button" className="ghost-button" onClick={() => void removeDocument(draftDocId)}>
                      Delete
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </article>
      </section>

      {error ? <p className="widget-error">{error}</p> : null}
    </div>
  );
}
